import { useMutation, useQueryClient } from "@tanstack/react-query";

import { OrderSet } from "@models/OrderSet";
import client from "@services/api";
import mapPaginatedRows from "@utils/reactQuery/mapPaginatedRows";
import { Paginated } from "@utils/reactQuery/types";

import { orderSetsKeyFactory } from "../../../queries/orderSetQueries";
import { useOptionalCurrentOrderSet } from "../CurrentOrderSetContext";
import { useOptionalOrderSetId } from "../useOrderSetId";

export default function useDeleteOrderSetMutation(id?: string) {
  const queryClient = useQueryClient();
  const orderSetId = useOptionalOrderSetId();
  const orderSetCtx = useOptionalCurrentOrderSet();
  return useMutation({
    mutationFn: () => client.delete(`/order-sets/${orderSetId ?? id}`),
    onSuccess: ({ id: deletedId }) => {
      const detailKey = orderSetsKeyFactory.detail(deletedId).queryKey;
      // The order-set page already has the record; the cache is the fallback
      // for callers that delete from a list.
      const deletedOrderSet =
        orderSetCtx?.orderSet ?? queryClient.getQueryData<OrderSet>(detailKey);

      // Navigate away first: removing a query the page still observes makes it
      // refetch the order-set that was just deleted.
      if (deletedOrderSet) {
        orderSetCtx?.onOrderSetDelete(deletedOrderSet);
      }

      queryClient.removeQueries({ queryKey: detailKey });

      // Drop from every cached draft list (not just active ones): a catalog
      // mounting later reads its list cache before the refetch lands, so a
      // stale row would let it add variants to a deleted order-set.
      queryClient.setQueriesData<Paginated<OrderSet>>(
        { queryKey: orderSetsKeyFactory.paginated._def },
        mapPaginatedRows((rows: OrderSet[]) =>
          rows.filter((os) => os.id !== deletedId)
        )
      );

      queryClient.invalidateQueries({
        queryKey: orderSetsKeyFactory.paginated._def,
      });
      queryClient.invalidateQueries({
        queryKey: orderSetsKeyFactory.list._def,
      });
    },
  });
}
